/*!
 * Copyright (c) Microsoft Corporation and contributors. All rights reserved.
 * Licensed under the MIT License.
 */
/**
 * Defines a place relative to sibling.
 * The "outside" of a trait is the `undefined` sibling,
 * so After `undefined` is the beginning of the trait, and before `undefined` is the end.
 *
 * For this purpose, traits look like:
 *
 * `{undefined} - {Node 0} - {Node 1} - ... - {Node N} - {undefined}`
 *
 * Each `{value}` in the diagram is a possible sibling, which is either a Node or undefined.
 * Each `-` in the above diagram is a `Place`, and can be describe as being `After` a particular `{sibling}` or `Before` it.
 * This means that `After` `{undefined}` means the same `Place` as before the first node
 * and `Before` `{undefined}` means the `Place` after the last Node.
 *
 * Each place can be specified, (aka 'anchored') in two ways (relative to the sibling before or after):
 * the choice of which way to anchor a place only matters when the kept across an edit, and thus evaluated in multiple contexts where the
 * two place description may no longer evaluate to the same place.
 * @public
 */
export var Side;
(function (Side) {
    Side[Side["Before"] = 0] = "Before";
    Side[Side["After"] = 1] = "After";
})(Side || (Side = {}));
/**
 * The status code of an attempt to apply the changes in an Edit.
 * @public
 */
export var EditStatus;
(function (EditStatus) {
    /**
     * The edit contained one or more malformed changes (e.g. was missing required fields such as `id`),
     * or contained a sequence of changes that could not possibly be applied sequentially without error
     * (e.g. an edit which tries to insert the same detached node twice).
     */
    EditStatus[EditStatus["Malformed"] = 0] = "Malformed";
    /**
     * The edit contained a well-formed sequence of changes that couldn't be applied to the current view,
     * generally because concurrent changes caused one or more merge conflicts.
     */
    EditStatus[EditStatus["Invalid"] = 1] = "Invalid";
    /**
     * The edit was applied to the current view successfully.
     */
    EditStatus[EditStatus["Applied"] = 2] = "Applied";
})(EditStatus || (EditStatus = {}));
/**
 * Types of ops handled by SharedTree.
 */
export var SharedTreeOpType;
(function (SharedTreeOpType) {
    /** An op that includes edit information. */
    SharedTreeOpType[SharedTreeOpType["Edit"] = 0] = "Edit";
    /** Includes a Fluid handle that corresponds to an edit chunk. */
    SharedTreeOpType[SharedTreeOpType["Handle"] = 1] = "Handle";
    /** An op that does not affect the tree's state. */
    SharedTreeOpType[SharedTreeOpType["NoOp"] = 2] = "NoOp";
    /** Signals that SharedTree contents should be updated to match a new write format. */
    SharedTreeOpType[SharedTreeOpType["Update"] = 3] = "Update";
})(SharedTreeOpType || (SharedTreeOpType = {}));
/**
 * Format versions that SharedTree supports writing. Changes to op or summary formats necessitate updates.
 * @public
 */
export var WriteFormat;
(function (WriteFormat) {
    /** Stores all edits in their raw format. */
    WriteFormat["v0_0_2"] = "0.0.2";
    /** Supports history virtualization, tree compression, string interning, and makes currentView optional. */
    WriteFormat["v0_1_1"] = "0.1.1";
})(WriteFormat || (WriteFormat = {}));
/**
 * {@inheritdoc ChangeType}
 * @public
 */
export var ChangeTypeInternal;
(function (ChangeTypeInternal) {
    ChangeTypeInternal[ChangeTypeInternal["Insert"] = 0] = "Insert";
    ChangeTypeInternal[ChangeTypeInternal["Detach"] = 1] = "Detach";
    ChangeTypeInternal[ChangeTypeInternal["Build"] = 2] = "Build";
    ChangeTypeInternal[ChangeTypeInternal["SetValue"] = 3] = "SetValue";
    ChangeTypeInternal[ChangeTypeInternal["Constraint"] = 4] = "Constraint";
    ChangeTypeInternal[ChangeTypeInternal["CompressedBuild"] = 5] = "CompressedBuild";
})(ChangeTypeInternal || (ChangeTypeInternal = {}));
/**
 * What to do when a Constraint is violated.
 * @public
 */
export var ConstraintEffect;
(function (ConstraintEffect) {
    /**
     * Discard Edit.
     */
    ConstraintEffect[ConstraintEffect["InvalidAndDiscard"] = 0] = "InvalidAndDiscard";
    /**
     * Discard Edit, but record metadata that application may want to try and recover this change by recreating it.
     * Should this be the default policy for when another (non Constraint) change is invalid?
     */
    ConstraintEffect[ConstraintEffect["InvalidRetry"] = 1] = "InvalidRetry";
    /**
     * Apply the change, but flag it for possible reconsideration by the app
     * (applying it is better than not, but perhaps the high level logic could produce something better).
     */
    ConstraintEffect[ConstraintEffect["ValidRetry"] = 2] = "ValidRetry";
})(ConstraintEffect || (ConstraintEffect = {}));
//# sourceMappingURL=0.0.2.js.map